'use client';
import styles from './properties-modal.module.css';
import { useProperties } from '@/context/properties-context';
import { JSX, MouseEvent, useRef, useState } from 'react';
import Modal from '@/components/modal/modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faChevronLeft,
	faChevronRight,
} from '@awesome.me/kit-012d1b9af0/icons/classic/light';
import Image from 'next/image';
import { UserDataType } from '@/types/user';

const regions = [
	{
		label: 'Cervical Nerves (C1-C8)',
		src: '/injuries/cervical.png',
		startPercent: 0,
		endPercent: 29.95,
		area: [
			{ top: '4%', left: '28.5%', value: 'c1' },
			{ top: '7.6%', left: '78.7%', value: 'c2' },
			{ top: '21.5%', left: '28.3%', value: 'c3' },
			{ top: '25%', left: '78.7%', value: 'c4' },
			{ top: '39%', left: '28.5%', value: 'c5' },
			{ top: '42.7%', left: '78.5%', value: 'c6' },
			{ top: '57%', left: '28.5%', value: 'c7' },
			{ top: '60.5%', left: '78.7%', value: 'c8' },
		],
	},
	{
		label: 'Thoracic Nerves (T1-T12)',
		src: '/injuries/thoracic.png',
		startPercent: 29.95,
		endPercent: 65.65,
		area: [
			{ top: '1%', left: '22.3%', value: 't1' },
			{ top: '6.7%', left: '81%', value: 't2' },
			{ top: '16.7%', left: '22.3%', value: 't3' },
			{ top: '22.3%', left: '81%', value: 't4' },
			{ top: '32.3%', left: '22.3%', value: 't5' },
			{ top: '37.9%', left: '81%', value: 't6' },
			{ top: '47.8%', left: '22.3%', value: 't7' },
			{ top: '53.7%', left: '81%', value: 't8' },
			{ top: '63.6%', left: '22.3%', value: 't9' },
			{ top: '69.3%', left: '81%', value: 't10' },
			{ top: '79.2%', left: '22.3%', value: 't11' },
			{ top: '84.95%', left: '81.1%', value: 't12' },
		],
	},
	{
		label: 'Lumbar Nerves (L1-L5)',
		src: '/injuries/lumbar.png',
		startPercent: 65.65,
		endPercent: 84,
		area: [
			{ top: '45.8%', left: '26.35%', value: 'l1' },
			{ top: '53.6%', left: '76.7%', value: 'l2' },
			{ top: '63.5%', left: '26.3%', value: 'l3' },
			{ top: '71.35%', left: '76.7%', value: 'l4' },
			{ top: '81.2%', left: '26.3%', value: 'l5' },
		],
	},
	{
		label: 'Sacral Nerves (S1-S5)',
		src: '/injuries/sacral.png',
		startPercent: 84,
		endPercent: 100,
		area: [
			{ top: '52.67%', left: '27.5%', value: 's1' },
			{ top: '70.3%', left: '77.8%', value: 's2' },
			{ top: '70.3%', left: '27.5%', value: 's3' },
			{ top: '88%', left: '77.9%', value: 's4' },
			{ top: '88%', left: '27.5%', value: 's5' },
		],
	},
];

type PropertiesModalProp = {
	isOpen: boolean;
	onClose: () => void;
};

type ModalData = {
	value: string;
	title: string;
	description: JSX.Element;
}[];

const severityData: ModalData = [
	{
		value: 'a',
		title: 'A',
		description: (
			<>Complete lack of motor and sensory function below the level of injury</>
		),
	},
	{
		value: 'b',
		title: 'B',
		description: (
			<>Some sensation below the level of the injury but not muscle movement</>
		),
	},
	{
		value: 'c',
		title: 'C',
		description: (
			<>
				Some muscle movement is spared below the level of injury, but 50 percent
				of the muscles below the level of injury cannot move against gravity.
			</>
		),
	},
	{
		value: 'd',
		title: 'D',
		description: (
			<>
				Most (more than 50 percent) of the muscles that are spared below the
				level of injury are strong enough to move against gravity.
			</>
		),
	},
];

const timeData: ModalData = [
	{
		value: 'acute',
		title: 'Accute',
		description: (
			<>
				Post-injury period within a <strong>few days</strong> after injury
			</>
		),
	},
	{
		value: 'subacute',
		title: 'Sub-Accute',
		description: (
			<>
				Post-injury period within a <strong>few weeks</strong> after injury, but
				possibly up to a couple of months
			</>
		),
	},
	{
		value: 'chronic',
		title: 'Chronic',
		description: (
			<>
				Any time after the sub-acute period when there is no longer any
				functional change in your body
			</>
		),
	},
];

export const InjuryModal = ({
	isOpen,
	onClose,
	selectedLevels = {},
	onLevelsUpdate,
	mode = 'filter',
}: PropertiesModalProp & {
	selectedLevels?: Record<string, boolean>;
	onLevelsUpdate?: (levels: Record<string, boolean>) => void;
	mode?: 'profile' | 'filter';
}) => {
	const imgRef = useRef<HTMLImageElement>(null);
	const [order, setOrder] = useState<number>(0);
	const [src, setSrc] = useState<string>('');

	const { updateProperty } = useProperties();

	function handleInjuryType(
		e: MouseEvent<HTMLImageElement, globalThis.MouseEvent>
	) {
		const img = imgRef.current;
		if (img) {
			const rect = img.getBoundingClientRect();
			const clickY = e.clientY - rect.top;
			const clickPercent = (clickY / rect.height) * 100;

			const region = regions.find(
				(r) => clickPercent >= r.startPercent && clickPercent < r.endPercent
			);

			if (region) {
				setSrc(region.src);
				setOrder(1);
			}
		}
	}

	const handleInjury = (nerve: string) => {
		if (mode === 'filter') {
			updateProperty('injury', nerve);
			setOrder(0);
			onClose();
		} else {
			const updatedLevels = {
				...selectedLevels,
				[nerve]: !selectedLevels[nerve],
			};

			onLevelsUpdate?.(updatedLevels);
		}
	};

	const handleClose = () => {
		setOrder(0);
		onClose();
	};

	return (
		<Modal isOpen={isOpen} onClose={handleClose} auto>
			<Modal.Title>
				<div className={styles['dialog-title']}>
					{mode === 'filter'
						? 'Filter by Injury'
						: 'Select where your injury is'}
				</div>
			</Modal.Title>
			<Modal.Body>
				<div className={styles['injury-body']}>
					{!order ? (
						<div className={styles['injury-image-container']}>
							<Image
								src={'/injuries/main.png'}
								priority
								ref={imgRef}
								alt='injury body level section'
								width={1650}
								height={2284}
								objectFit='contain'
								onClick={handleInjuryType}
							/>
						</div>
					) : (
						<div className={styles['injury-secondary']}>
							<div className={styles['injury-image-container']}>
								<Image
									src={src}
									priority
									alt='injury body region diagram'
									width={1650}
									height={2284}
									objectFit='contain'
								/>
							</div>

							<div className={styles['injury-secondary-wrapper']}>
								{regions
									.find((region) => region.src === src)
									?.area.map(({ top, left, value }) => (
										<div
											key={value}
											className={styles.bubble}
											style={{ top, left }}
											onClick={() => handleInjury(value)}
										/>
									))}
							</div>
						</div>
					)}
				</div>
			</Modal.Body>

			<Modal.Footer>
				{mode === 'profile' && order === 1 && (
					<>
						<button
							className={styles['back-button']}
							onClick={() => setOrder(0)}
						>
							back
						</button>
						<button className={styles['done-button']} onClick={handleClose}>
							Done
						</button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export const SeverityModal = ({
	isOpen,
	onClose,
	selectedAsia,
	onAsiaUpdate,
	mode = 'filter',
}: PropertiesModalProp & {
	selectedAsia?: UserDataType['asia'];
	onAsiaUpdate?: (asia: {
		a: boolean;
		b: boolean;
		c: boolean;
		d: boolean;
	}) => void;
	mode?: 'profile' | 'filter';
}) => {
	const { updateProperty } = useProperties();

	function handleSelect(value: string) {
		if (mode === 'filter') {
			updateProperty('severity', value);
		} else {
			const updatedAsia = {
				a: value === 'a',
				b: value === 'b',
				c: value === 'c',
				d: value === 'd',
			};

			onAsiaUpdate?.(updatedAsia);
		}
		onClose();
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} auto>
			<Modal.Title>
				<div className={styles['dialog-title']}>
					{mode === 'filter' ? 'Filter by Severity' : 'Select Injury Severity'}
				</div>
			</Modal.Title>
			<Modal.Body>
				<div className={styles['severity-body']}>
					<p>
						The severity of a spinal cord injury is commonly classified using
						the ASIA Impairment Scale
					</p>

					<div className={styles['severity-options']}>
						{severityData.map(({ title, value, description }) => (
							<div key={value} className={styles['severity-option']}>
								<button onClick={() => handleSelect(value)}>
									Grade {title}
								</button>
								<p>{description}</p>
							</div>
						))}
					</div>

					<SmallModalData
						type='severity'
						data={severityData}
						onClose={onClose}
						mode={mode}
						selectedAsia={selectedAsia}
						onAsiaUpdate={onAsiaUpdate}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer>
				{mode === 'filter' && (
					<div className={styles['severity-footer']}>
						<p>
							Click the classification that best matches your situation or if
							you are not sure{' '}
							<button
								className={styles['select-all']}
								onClick={() => handleSelect('')}
							>
								select all
							</button>
						</p>
					</div>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export const TimeModal = ({ isOpen, onClose }: PropertiesModalProp) => {
	const { updateProperty } = useProperties();

	function handleSelect(value: string) {
		updateProperty('time', value);
		onClose();
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} auto>
			<Modal.Title>
				<div className={styles['dialog-title']}>Time Since Injury</div>
			</Modal.Title>
			<Modal.Body>
				<div className={styles['time-body']}>
					<p>
						After a spinal cord injury the body undergoes continuous changes.
						Certain treatments may only work if applied in a specific time
						windows (i.e. days - weeks - months) following injury.
						<br />
						Time windows after injury are classified by the following:
					</p>

					<div className={styles['time-options']}>
						{timeData.map(({ title, value, description }) => (
							<div key={value} className={styles['time-option']}>
								<button onClick={() => handleSelect(value)}>{title}</button>
								<p>{description}</p>
							</div>
						))}
					</div>

					<SmallModalData type='time' data={timeData} onClose={onClose} />
				</div>
			</Modal.Body>
			<Modal.Footer>
				<div className={styles['time-footer']}>
					<p>
						The lengths of time for each of these have no standard definition.
						Different trials might use them to mean different lengths of time.
					</p>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export const SmallModalData = ({
	type,
	data,
	onClose,
	mode = 'filter',
	selectedAsia,
	onAsiaUpdate,
}: {
	type: 'severity' | 'time';
	data: ModalData;
	onClose: () => void;
	mode?: 'profile' | 'filter';
	selectedAsia?: UserDataType['asia'];
	onAsiaUpdate?: (asia: {
		a: boolean;
		b: boolean;
		c: boolean;
		d: boolean;
	}) => void;
}) => {
	const { properties, updateProperty } = useProperties();

	const [index, setIndex] = useState(() => {
		if (selectedAsia) {
			const selectedGrade = Object.entries(selectedAsia).find(
				([_, isSelected]) => isSelected
			)?.[0];
			if (selectedGrade) {
				const foundIndex = data.findIndex(
					(item) => item.value === selectedGrade
				);
				if (foundIndex !== -1) return foundIndex;
			}
		}

		if (properties[type]) {
			const foundIndex = data.findIndex(
				(item) => item.value === properties[type]
			);
			return foundIndex !== -1 ? foundIndex : 0;
		}

		return 0;
	});

	const [direction, setDirection] = useState<'left' | 'right' | null>(null);

	const prevDisabled = index === 0;
	const nextDisabled = index === data.length - 1;

	const navigate = (newIndex: number, navDirection: 'left' | 'right') => {
		setDirection(navDirection);
		setIndex(newIndex);
		setTimeout(() => setDirection(null), 300);
	};

	const goNext = () => {
		const newIndex = index === data.length - 1 ? 0 : index + 1;
		navigate(newIndex, 'left');
	};

	const goPrev = () => {
		const newIndex = index === 0 ? data.length - 1 : index - 1;
		navigate(newIndex, 'right');
	};

	const handleSelect = () => {
		const selectedValue = data[index].value;

		if (mode === 'filter') {
			updateProperty(type, selectedValue);
		} else {
			const updatedAsia = {
				a: selectedValue === 'a',
				b: selectedValue === 'b',
				c: selectedValue === 'c',
				d: selectedValue === 'd',
			};
			onAsiaUpdate?.(updatedAsia);
		}
		onClose();
	};

	return (
		<div className={styles['small']}>
			<div
				className={`${styles['small-container']} ${
					direction ? styles[`slide-${direction}`] : ''
				}`}
			>
				<p className={styles['small-title']}>
					{type === 'severity' && 'AIS Grade '}
					{data[index].title}
				</p>
				<p>{data[index].description}</p>
			</div>

			<div className={styles['small-handle']}>
				<FontAwesomeIcon
					icon={faChevronLeft}
					size='xl'
					className={`${styles['small-prev']} ${
						prevDisabled ? styles.disabled : ''
					}`}
					onClick={goPrev}
				/>

				<button className={styles['small-select']} onClick={handleSelect}>
					{mode === 'filter' ? 'Select' : 'Choose'}
				</button>

				<FontAwesomeIcon
					icon={faChevronRight}
					size='xl'
					className={`${styles['small-next']} ${
						nextDisabled ? styles.disabled : ''
					}`}
					onClick={goNext}
				/>
			</div>
		</div>
	);
};
